import { useMemo } from 'react';
import { Button, InlineStack, Text } from '@customerio/pluma-components/react';
import { ConditionNodeRenderer, parseConditions } from '@/features/condition-viewer';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { decodeFilters, isEmptyFiltersQuery } from '../utils/list-params';

type FilterConditionsSummaryProps = {
  /** The Base64-encoded `filters` search param. */
  filters: string | null;
  onClearFilters: () => void;
};

/**
 * Read-only rendering of the active condition filters, with a clear
 * affordance. The interactive condition builder is owned by the Condition
 * Editor Overhaul project and lands as a follow-up; until then deep-linked
 * and Ember-built filters render (and keep filtering) here.
 */
export function FilterConditionsSummary({ filters, onClearFilters }: FilterConditionsSummaryProps) {
  const workspaceId = requireWorkspaceId();
  const hasFilters = !isEmptyFiltersQuery(filters);

  const parsed = useMemo(() => {
    if (!hasFilters || filters == null) return null;
    const decoded = decodeFilters(filters);
    return parseConditions(Array.isArray(decoded) ? { and: decoded } : decoded);
  }, [filters, hasFilters]);

  if (parsed == null) {
    return (
      <Text color="subtle">
        Condition filtering for this page is coming soon. Search by email address in the meantime.
      </Text>
    );
  }

  return (
    <InlineStack gap="150" alignBlock="start">
      <ConditionNodeRenderer node={parsed} workspaceId={workspaceId} />
      <Button variant="subtle" size="sm" onClick={onClearFilters}>
        Clear
      </Button>
    </InlineStack>
  );
}
